import React, { useState, useEffect } from 'react';
import api from '../api';

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MEAL_TYPES = [
  { value: 'petit_dejeuner', label: 'Petit-déj', emoji: '&#9749;' },
  { value: 'dejeuner', label: 'Déjeuner', emoji: '&#127858;' },
  { value: 'gouter', label: 'Goûter', emoji: '&#127850;' },
  { value: 'diner', label: 'Dîner', emoji: '&#127789;' },
];

function getWeekDates(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7) + offset * 7);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

export default function MealsPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [meals, setMeals] = useState([]);
  const [showModal, setShowModal] = useState(null);
  const [form, setForm] = useState({ title: '', ingredients: '', notes: '' });

  const dates = getWeekDates(weekOffset);
  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    api.get('/meals', { params: { week_start: dates[0] } })
      .then(r => setMeals(r.data)).catch(() => {});
  }, [weekOffset]); // eslint-disable-line

  const getMeal = (date, type) => meals.find(m => m.meal_date === date && m.meal_type === type);

  const openAdd = (date, type) => {
    const existing = getMeal(date, type);
    setForm({ title: existing?.title || '', ingredients: existing?.ingredients || '', notes: existing?.notes || '' });
    setShowModal({ date, type });
  };

  const saveMeal = async (e) => {
    e.preventDefault();
    await api.post('/meals', { meal_date: showModal.date, meal_type: showModal.type, ...form });
    setShowModal(null);
    const r = await api.get('/meals', { params: { week_start: dates[0] } });
    setMeals(r.data);
  };

  const addToShopping = async (mealId) => {
    try {
      await api.post(`/meals/${mealId}/to-shopping`);
      alert('Ingrédients ajoutés à la liste de courses !');
    } catch {
      alert('Erreur ou pas d\'ingrédients renseignés');
    }
  };

  const weekLabel = () => {
    const start = new Date(dates[0] + 'T00:00');
    const end = new Date(dates[6] + 'T00:00');
    return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  return (
    <div>
      <div className="page-header">
        <h1>Repas de la semaine</h1>
        <p>Planifiez vos repas et générez votre liste de courses</p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setWeekOffset(weekOffset - 1)}>&larr; Semaine préc.</button>
        <span style={{ fontWeight: 700, fontSize: '0.9rem', minWidth: 200, textAlign: 'center' }}>{weekLabel()}</span>
        <button className="btn btn-secondary btn-sm" onClick={() => setWeekOffset(weekOffset + 1)}>Semaine suiv. &rarr;</button>
        {weekOffset !== 0 && <button className="btn btn-xs btn-secondary" onClick={() => setWeekOffset(0)}>Aujourd'hui</button>}
      </div>

      <div className="meals-week">
        {dates.map((date, i) => (
          <div key={date} className="meal-day-col">
            <div className={`meal-day-header ${date === todayStr ? 'today' : ''}`}>
              {DAYS[i]}
              <div style={{ fontWeight: 400, fontSize: '0.7rem' }}>
                {new Date(date + 'T00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
              </div>
            </div>
            {MEAL_TYPES.map(type => {
              const meal = getMeal(date, type.value);
              return (
                <div key={type.value} className="meal-slot" onClick={() => openAdd(date, type.value)}>
                  <div className="meal-slot-type" dangerouslySetInnerHTML={{ __html: type.emoji + ' ' + type.label }} />
                  {meal ? (
                    <div className="meal-slot-title">{meal.title}</div>
                  ) : (
                    <div className="meal-slot-empty">+ Ajouter</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{MEAL_TYPES.find(t => t.value === showModal.type)?.label} — {new Date(showModal.date + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
            <form onSubmit={saveMeal}>
              <div className="form-group">
                <label>Plat</label>
                <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Poulet rôti, Salade César..." required />
              </div>
              <div className="form-group">
                <label>Ingrédients</label>
                <textarea className="form-input" value={form.ingredients} onChange={e => setForm({ ...form, ingredients: e.target.value })} placeholder={"Un ingrédient par ligne :\nPoulet 1kg\nPommes de terre\nHuile d'olive"} style={{ minHeight: 100 }} />
                <div className="form-hint">Un ingrédient par ligne — sera ajouté à la liste de courses</div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Préparation, allergies..." />
              </div>
              <div className="modal-actions">
                {getMeal(showModal.date, showModal.type) && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => addToShopping(getMeal(showModal.date, showModal.type).id)} style={{ marginRight: 'auto' }}>
                    &#128722; Ajouter aux courses
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(null)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
